import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { MergeStrategy, Pull } from '@/features/pulls/services/pulls';
import { repositoryQuery } from '@/services/repo';

export const mergeStrategyDetails = {
  merge: {
    title: 'Merge',
    description: 'All commits from this branch will be merged via merge commit',
  },
  squash: {
    title: 'Squash and Merge',
    description: 'All commits from this branch will be combined into a single commit',
  },
  rebase: {
    title: 'Rebase and Merge',
    description: 'All commits from this branch will be rebased and merged',
  },
};

export function MergeStrategyOption({ strategy }: { strategy: MergeStrategy }) {
  const { title, description } = mergeStrategyDetails[strategy];

  return (
    <>
      <p className="text-sm font-bold">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </>
  );
}

export type MergeStrategyDropdownProps = {
  strategies: MergeStrategy[];
  onSelect: (strategy: MergeStrategy) => void;
  children: React.ReactNode;
};

export function MergeStrategyDropdown({ strategies, onSelect, children }: MergeStrategyDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onSelect={() => strategies.includes('merge') && onSelect('merge')}
          disabled={!strategies.includes('merge')}
          className="flex flex-col items-start gap-1"
        >
          <MergeStrategyOption strategy="merge" />
        </DropdownMenuItem>
        <Separator />
        <DropdownMenuItem
          onSelect={() => strategies.includes('squash') && onSelect('squash')}
          disabled={!strategies.includes('squash')}
          className="flex flex-col items-start gap-1"
        >
          <MergeStrategyOption strategy="squash" />
        </DropdownMenuItem>
        <Separator />
        <DropdownMenuItem
          onSelect={() => strategies.includes('rebase') && onSelect('rebase')}
          disabled={!strategies.includes('rebase')}
          className="flex flex-col items-start gap-1"
        >
          <MergeStrategyOption strategy="rebase" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type MergeStrategySelectProps = {
  onSelect: (strategy: MergeStrategy) => void;
  value: MergeStrategy;
};

export function MergeStrategySelect({ onSelect, value }: MergeStrategySelectProps) {
  const { owner, repo } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  // Fetch repository details to get merge settings
  const { data: repository } = useQuery(repositoryQuery(owner, repo));

  // Extract repository settings with fallback to true for safety
  const allowMergeCommit = repository?.allow_merge_commit ?? true;
  const allowSquashMerge = repository?.allow_squash_merge ?? true;
  const allowRebaseMerge = repository?.allow_rebase_merge ?? true;

  // Determine available strategies
  const availableStrategies: MergeStrategy[] = [];
  if (allowMergeCommit) availableStrategies.push('merge');
  if (allowSquashMerge) availableStrategies.push('squash');
  if (allowRebaseMerge) availableStrategies.push('rebase');

  // If the current value is not allowed, use the first available strategy
  const { title } = mergeStrategyDetails[value];

  return (
    <Select onValueChange={onSelect} value={value}>
      <SelectTrigger>
        <SelectValue aria-label={title}>
          <p>{title}</p>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="merge" disabled={!allowMergeCommit}>
          <MergeStrategyOption strategy="merge" />
        </SelectItem>
        <SelectItem value="squash" disabled={!allowSquashMerge}>
          <MergeStrategyOption strategy="squash" />
        </SelectItem>
        <SelectItem value="rebase" disabled={!allowRebaseMerge}>
          <MergeStrategyOption strategy="rebase" />
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
